import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Clock, CheckCircle2 } from "lucide-react";
import HTMLContent from "../../components/HTMLContent";
import NavBar from "../../components/NavBar";
import OnboardingLayout from "./OnboardingLayout";
import CustomButton from "../../components/CustomButton";
import { STRING, STRINGS } from "../../constant";
import { toHHMMSS } from "../../constant/strings";
import QuestionOptions from "../../components/common/QuestionOptions";
import logger from "../../utils/logger";
import { ThemeContext } from "../../context/ThemeProvider";
import { GET_ONBOARDING_TEST_DETAILS } from "../../api/test";
import InfoDialog from "../../components/InfoDialog";
import CommonHeader from "../../components/common/CommonHeader";
import { TestProgress, TestExitModal, TestEndModal, TestTimeUpModal } from "../../components/test";
import { faClipboardCheck } from "@fortawesome/free-solid-svg-icons";
import { useAnalytics } from "../../context/AnalyticsProvider";

// Progress steps configuration for the loading screen
const progressSteps = [
  {
    title: "Calculating your score and performance metrics",
    description: "Analyzing your answers and computing detailed statistics",
    time: 100
  },
  {
    title: "Gathering community performance data",
    description: "Collecting insights from thousands of other learners",
    time: 120
  },
  {
    title: "Preparing personalized insights",
    description: "Creating tailored recommendations based on your performance",
    time: 150
  },
  {
    title: "Generating your relative performance analysis",
    description: "Comparing your results with the global community",
    time: 130
  }
];

const OnboardingTest = () => {
  const { theme } = useContext(ThemeContext);
  const { trackAssessmentStart } = useAnalytics();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastIndex, setLastIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isTimeup, setTimeup] = useState(false);
  const [isTestExitPopup, setTestExitPopup] = useState(false);
  const [isTestEndPopup, setTestEndPopup] = useState(false);
  const [isSubmitAnswer, setSubmitAnswer] = useState({});
  const [isSavingTest, setIsSavingTest] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [history, setHistory] = useState({
    test_questions_meta_data: [],
    remaining_time: 0,
    total_time: 0,
    test_name: '',
    correct: 0,
    incorrect: 0,
    unanswered: 0,
    total: 0,
    score: 0
  });

  const OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

  const navigate = useNavigate();
  const { selectedExam } = useLocation().state || {};
  const historyRef = useRef(null);
  historyRef.current = history;

  const timeRef = useRef(null);
  timeRef.current = history?.remaining_time;

  const lastIndexRef = useRef(null);
  lastIndexRef.current = lastIndex;

  // Memoize currentItem calculation
  const currentItem = useMemo(() => {
    if (
      history?.test_questions_meta_data?.length > 0 &&
      !isNaN(currentIndex) &&
      history?.test_questions_meta_data?.length > currentIndex
    ) {
      const question = history?.test_questions_meta_data[currentIndex]?.question;
      const choices = history?.test_questions_meta_data[currentIndex]?.choices;
      const correct_answer = history?.test_questions_meta_data[currentIndex]?.correct_answer;
      const topic_id = history?.test_questions_meta_data[currentIndex]?.topic_id;
      
      return { question: question, choices: choices, correct_answer: correct_answer, topic_id: topic_id };
    }
    return null;
  }, [history?.test_questions_meta_data, currentIndex]);

  // Styles using new color system
  const styles = {
    questionCard: {
      background: theme.dashboard.card.background,
      border: `1px solid ${theme.dashboard.card.border}`,
      borderRadius: '1rem',
      boxShadow: theme.dashboard.card.shadow,
      marginBottom: '2rem',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    questionContent: {
      padding: '2rem',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    questionText: {
      marginBottom: '2rem',
      color: theme.text.primary,
      fontSize: '1.125rem',
      lineHeight: '1.7',
    },
    optionsGrid: {
      display: 'grid',
      gap: '1rem',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      alignItems: 'stretch',
    },
    submitContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '2rem',
    }
  };

  // Save test data to localStorage
  const saveToLocalStorage = (testData) => {
    try {
      const onboardingTestData = {
        timestamp: Date.now(),
        testData: testData,
        questionsData: testData?.test_questions_meta_data?.map(item => ({
          question_id: item?.question_id,
          topic_id: item?.topic_id,
          correct: item?.response === STRING.correct_tag,
          selected: item?.response,
        })) || []
      };
      
      localStorage.setItem('onboarding_test_result', JSON.stringify(onboardingTestData));
      localStorage.setItem('onboarding_test_selected_exam', JSON.stringify(selectedExam));
      logger.log('Onboarding test data saved to localStorage', onboardingTestData);
    } catch (error) {
      logger.error('Failed to save onboarding test data to localStorage', error);
    }
  };

  async function onSaveTestHistory(isTimeup) {
    setIsSavingTest(true);
    
    // Wait for the progress steps to complete
    await new Promise(resolve => setTimeout(resolve, 5000));
    
    let historyData = { ...historyRef.current };

    const totalItems = historyData?.test_questions_meta_data ?? [];
    
    const correct =
      totalItems?.filter((item) => item.response === STRING.correct_tag)
        ?.length ?? 0;
    const incorrect =
      totalItems?.filter(
        (item) => item.response === STRING.incorrect_tag
      )?.length ?? 0;
    const unanswered =
      totalItems?.filter(
        (item) => item.response === STRING.unanswered_tag
      )?.length ?? 0;

    historyData.correct = correct;
    historyData.incorrect = incorrect;
    historyData.unanswered = unanswered;
    historyData.total = totalItems.length;
    historyData.score = parseInt((correct * 100) / totalItems.length);
    historyData.time_taken = historyData.total_time - historyData.remaining_time;

    saveToLocalStorage(historyData);
    setIsSavingTest(false);

    if (isTimeup) {
      setTimeup(true);
    } else {
      navigate("/onboarding-test-review", { state: { historyData, selected_exam: selectedExam} });
    }
  }

  function onBack() {
    setTestExitPopup(true);
  }

  function onExit() {
    navigate(-1);
  }

  function onPrevious() {
    setSelectedIndex(null);
    setCurrentIndex((state) => {
      return state === 0 ? 0 : state - 1;
    });
  }

  function onNext() {
    setSelectedIndex(null);

    if (currentIndex + 1 >= lastIndexRef.current) {
      setLastIndex(currentIndex + 1);
    }

    setCurrentIndex((state) => {
      return state === history?.test_questions_meta_data.length - 1
        ? 0
        : state + 1;
    });
  }

  function onEndTest() {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setTimeout(() => {
        setTestEndPopup(true);
      }, 100);
    }, 500);
  }

  function onSubmitQuestion(index, questionData) {
    setHistory(prev => {
      const updated = { ...prev };
      updated.test_questions_meta_data[index] = questionData;
      return updated;
    });
  }

  function onChangeTime() {
    setHistory(prev => ({
      ...prev,
      remaining_time: prev.remaining_time - 1
    }));
  }

  const handleAnswerSelection = (index, currentItem, history, currentIndex) => {
    const selected = OPTIONS[index];
    const correct = currentItem?.correct_answer;
    const item = history?.test_questions_meta_data[currentIndex];

    if (Array.isArray(correct)) {
      handleMultiSelectAnswer(selected, correct, item, currentIndex);
    } else {
      handleSingleSelectAnswer(selected, correct, item, currentIndex);
    }
  };

  const handleMultiSelectAnswer = (selected, correct, item, currentIndex) => {
    const previousSelectedOptions = item?.selected_choice === 'X' ? [] : (item?.selected_choice ?? []);
    const selectedOptions = previousSelectedOptions.includes(selected)
      ? previousSelectedOptions.filter(e => e !== selected)
      : [...previousSelectedOptions, selected];

    item.selected_choice = selectedOptions;
    item.response = selectedOptions.length > 0
      ? isMultiSelectCorrect(correct, selectedOptions)
        ? STRING.correct_tag
        : STRING.incorrect_tag
      : STRING.unanswered_tag;

    updateQuestionState(currentIndex, item, selectedOptions);
  };

  const handleSingleSelectAnswer = (selected, correct, item, currentIndex) => {
    item.selected_choice = selected ?? "X";
    item.response = (selected !== "X" && selected != null)
      ? correct === selected
        ? STRING.correct_tag
        : STRING.incorrect_tag
      : STRING.unanswered_tag;

    updateQuestionState(currentIndex, item, selected);
  };

  const isMultiSelectCorrect = (correct, selectedOptions) => {
    if (correct?.length !== selectedOptions?.length) return false;
    return correct.every(element => selectedOptions.includes(element));
  };

  const updateQuestionState = (currentIndex, item, selected) => {
    onSubmitQuestion(currentIndex, item);
    setSelectedIndex({ selected, is_from_older: false });
  };

  useEffect(() => {
    if (history?.test_questions_meta_data?.length > 0) {
      setSelectedIndex({
        selected: history?.test_questions_meta_data[currentIndex]?.selected_choice,
        is_from_older: true,
      });
    }
  }, [currentIndex]);

  useEffect(() => {
    let timer = null;

    if (history.total_time) {
      timer = setInterval(() => {
        if (timeRef.current <= 0) {
          clearInterval(timer);
          setLoading(true);
          setTimeout(() => {
            setLoading(false);
            setTimeout(() => {
              setTimeup(true);
            }, 500);
          }, 1000);
        } else {
          onChangeTime();
        }
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [history.total_time]);

  useEffect(() => {
    fetchQuestionList();
  }, []);

  async function fetchQuestionList() {
    const result = GET_ONBOARDING_TEST_DETAILS(selectedExam?.key);
    result.test_name = selectedExam?.name + " Diagnostic Test";
    result.date = new Date().toLocaleDateString();
    setHistory(result);
    
    // Track assessment start
    await trackAssessmentStart({
      assessment_type: 'diagnostic',
      exam_type: selectedExam?.key,
      question_count: result?.test_questions_meta_data?.length || 30
    });
  }

  const time = toHHMMSS(history?.remaining_time);
  const answeredQuestions = history?.test_questions_meta_data?.filter(
    (item) => item.response !== STRING.unanswered_tag
  )?.length || 0;

  return (
    <OnboardingLayout>
      <div className="min-h-screen transition-colors duration-300" >
        <div className="mx-4 sm:mx-4 md:mx-8 lg:mx-8 mb-4 sm:mb-4 md:mb-8 lg:mb-8 mt-20">
          
          <CommonHeader
            title={history?.test_name}
            icon={faClipboardCheck}
            iconType="fontawesome"
            onBack={onBack}
            theme={theme}
          />

          {/* Progress */}
          <TestProgress
            currentIndex={currentIndex}
            totalQuestions={history?.test_questions_meta_data?.length || 0}
            answeredQuestions={answeredQuestions}
            remainingTime={time}
            theme={theme}
          />

          {/* Question Content */}
          <div>
            {currentItem ? (
              <div style={styles.questionCard}>
                <div style={styles.questionContent}>
                  <div style={styles.questionText}>
                    <HTMLContent
                      htmlContent={currentItem?.question}
                      style={{ color: theme.text.primary }}
                    />
                  </div>

                  <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                    {currentItem?.choices?.map((options, index) => {
                      if (!options) return null;

                      return (
                        <QuestionOptions
                          key={index + JSON.stringify(options)}
                          testName="onboarding-test"
                          index={index}
                          item={options}
                          isShowAnswer={false}
                          correct={currentItem.correct_answer}
                          selected={history?.test_questions_meta_data[currentIndex]?.selected_choice}
                          isSubmit={(isSubmitAnswer?.[currentIndex] ?? false)}
                          onPress={() => handleAnswerSelection(index, currentItem, history, currentIndex)}
                          theme={theme}
                        />
                      );
                    })}
                  </div>

                </div>
              </div>
            ) : (
              <div style={styles.questionCard}>
                <div style={styles.questionContent}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    padding: '3rem',
                    color: theme.text.secondary,
                    fontSize: '1.125rem'
                  }}>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                    Loading question...
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Navigation */}
          <NavBar
            type="test"
            onNext={onNext}
            onPrevious={onPrevious}
            currentIndex={currentIndex}
            dataSet={history?.test_questions_meta_data?.length ?? 0}
            navTitle="Question"
            showEnd={true}
            onEndTest={onEndTest}
            isLoading={isLoading}
          />

          {/* Loading Overlay */}
          {isLoading && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}
            >
              <div 
                style={{
                  background: theme.dashboard.card.background,
                  padding: '2rem',
                  borderRadius: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem',
                  boxShadow: theme.dashboard.card.shadow,
                }}
              >
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <div style={{ color: theme.text.primary, fontWeight: 600 }}>
                  Processing your test results...
                </div>
              </div>
            </div>
          )}

          {/* Modals */}
          <TestExitModal
            isOpen={isTestExitPopup}
            onClose={() => setTestExitPopup(false)}
            onConfirm={() => {
              navigate(-1, { state: { isFromBack: true } });
              setTestExitPopup(false);
            }}
            theme={theme}
          />

          <TestEndModal
            isOpen={isTestEndPopup}
            onClose={() => setTestEndPopup(false)}
            onConfirm={() => {
              setTestEndPopup(false);
              onSaveTestHistory(false);
            }}
            theme={theme}
          />

          <TestTimeUpModal
            isOpen={isTimeup}
            onConfirm={() => {
              setTimeup(false);
              onSaveTestHistory(true);
            }}
            theme={theme}
          />

          {/* Progress Dialog for Saving */}
          <AnimatePresence>
            {isSavingTest && (
              <InfoDialog
                isOpen={isSavingTest}
                title="Calculating Your Performance"
                description="We're analyzing your test results and comparing them with our community of learners to give you the most comprehensive performance insights."
                showActions={false}
                progressSteps={progressSteps}
                theme={theme}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingTest;